// Search tab component - search TikTok videos with multi-select
import { authHeaders } from "../config";

import { useState, useCallback, useEffect } from "react";
import type { SearchVideo } from "@dishsnap/shared";
import { VideoCard } from "./VideoCard.js";
import styles from "./SearchTab.module.css";
import { apiUrl } from "../config";

const STORAGE_KEY = "dishsnap_selected_videos";

type SearchType = "keywords" | "user" | "hashtag";

// Fetch recent searches from API
async function fetchRecentItems(type: "user" | "hashtag"): Promise<string[]> {
  try {
    const response = await fetch(apiUrl(`/api/recent-searches?type=${type}`));
    if (!response.ok) return [];
    const data = await response.json();
    return data.items || [];
  } catch {
    return [];
  }
}

// Save recent search to API
async function saveRecentItem(type: "user" | "hashtag", value: string): Promise<void> {
  try {
    await fetch(apiUrl("/api/recent-searches"), {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ type, value }),
    });
  } catch {
    // Ignore errors
  }
}

// Remove a recent item via API
async function removeRecentItem(type: "user" | "hashtag", value: string): Promise<void> {
  try {
    await fetch(apiUrl("/api/recent-searches"), {
      method: "DELETE",
      headers: authHeaders(),
      body: JSON.stringify({ type, value }),
    });
  } catch {
    // Ignore errors
  }
}

// Extract video ID from TikTok URL
function extractVideoId(url: string | undefined): string | null {
  if (!url) return null;
  // Match patterns like /video/7123456789 or just the numeric ID
  const match = url.match(/\/video\/(\d+)/);
  return match && match[1] ? match[1] : null;
}

interface SearchTabProps {
  onProcessVideos: (videos: SearchVideo[]) => void;
  disabled?: boolean;
}

// Load selected videos from localStorage
function loadSelectedVideos(): SearchVideo[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save selected videos to localStorage
function saveSelectedVideos(videos: SearchVideo[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(videos));
  } catch {
    // Ignore storage errors
  }
}

export function SearchTab({ onProcessVideos, disabled }: SearchTabProps) {
  const [searchType, setSearchType] = useState<SearchType>("keywords");
  const [usedVideoIds, setUsedVideoIds] = useState<Set<string>>(new Set());
  const [recentUsers, setRecentUsers] = useState<string[]>([]);
  const [recentHashtags, setRecentHashtags] = useState<string[]>([]);

  // Fetch jobs source URLs and recent searches on mount
  useEffect(() => {
    async function fetchInitialData() {
      try {
        // Fetch used videos
        const jobsResponse = await fetch(apiUrl("/api/jobs?limit=500"));
        const jobsData = await jobsResponse.json();
        const jobs = jobsData.jobs || [];
        const ids = new Set<string>();
        for (const job of jobs) {
          if (job.sourceUrl) {
            const videoId = extractVideoId(job.sourceUrl);
            if (videoId) {
              ids.add(videoId);
            }
          }
        }
        setUsedVideoIds(ids);

        // Fetch recent users and hashtags from database
        const [users, hashtags] = await Promise.all([
          fetchRecentItems("user"),
          fetchRecentItems("hashtag"),
        ]);
        setRecentUsers(users);
        setRecentHashtags(hashtags);
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      }
    }
    fetchInitialData();
  }, []);
  const [query, setQuery] = useState("");
  const [videos, setVideos] = useState<SearchVideo[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<SearchVideo[]>(loadSelectedVideos);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);

  // Save to localStorage whenever selection changes
  useEffect(() => {
    saveSelectedVideos(selectedVideos);
  }, [selectedVideos]);

  const getSearchUrl = useCallback((q: string, count: number, cursorVal?: string) => {
    const cursorParam = cursorVal ? `&cursor=${cursorVal}` : "";
    switch (searchType) {
      case "user":
        return apiUrl(`/api/search/user?username=${encodeURIComponent(q)}&count=${count}${cursorParam}`);
      case "hashtag":
        return apiUrl(`/api/search/hashtag?tag=${encodeURIComponent(q)}&count=${count}${cursorParam}`);
      default:
        return apiUrl(`/api/search?q=${encodeURIComponent(q)}&count=${count}${cursorParam}`);
    }
  }, [searchType]);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setCursor(undefined);
    setHasMore(false);

    try {
      const response = await fetch(getSearchUrl(query, 20));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Search failed");
      }

      console.log("Search response:", { videos: data.videos?.length, cursor: data.cursor, hasMore: data.hasMore });
      setVideos(data.videos || []);
      setCursor(data.cursor);
      setHasMore(data.hasMore === true);

      // Save to recent searches on successful search
      if (searchType === "user") {
        await saveRecentItem("user", query);
        setRecentUsers(await fetchRecentItems("user"));
      } else if (searchType === "hashtag") {
        await saveRecentItem("hashtag", query);
        setRecentHashtags(await fetchRecentItems("hashtag"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, getSearchUrl, searchType]);

  const handleLoadMore = useCallback(async () => {
    if (!cursor || isLoadingMore) return;

    setIsLoadingMore(true);
    setError(null);

    try {
      const response = await fetch(getSearchUrl(query, 20, cursor));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Load more failed");
      }

      // Append new videos, avoiding duplicates
      const newVideos = (data.videos || []).filter(
        (v: SearchVideo) => !videos.some(existing => existing.videoId === v.videoId)
      );
      setVideos(prev => [...prev, ...newVideos]);
      setCursor(data.cursor);
      setHasMore(data.hasMore === true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Load more failed");
    } finally {
      setIsLoadingMore(false);
    }
  }, [query, cursor, videos, isLoadingMore, getSearchUrl]);

  // Click on a recent user/hashtag to search it
  const handleRecentClick = useCallback((item: string) => {
    setQuery(item);
    // Trigger search with the item
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }, 0);
  }, []);

  // Remove a recent user/hashtag
  const handleRemoveRecent = useCallback(async (item: string, type: "user" | "hashtag") => {
    await removeRecentItem(type, item);
    if (type === "user") {
      setRecentUsers(await fetchRecentItems("user"));
    } else {
      setRecentHashtags(await fetchRecentItems("hashtag"));
    }
  }, []);

  const handleToggleSelect = useCallback((video: SearchVideo) => {
    setSelectedVideos((prev) => {
      const isSelected = prev.some((v) => v.videoId === video.videoId);
      if (isSelected) {
        return prev.filter((v) => v.videoId !== video.videoId);
      } else {
        return [...prev, video];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allSelected = videos.every((v) =>
      selectedVideos.some((sv) => sv.videoId === v.videoId)
    );

    if (allSelected) {
      // Deselect all from current search
      setSelectedVideos((prev) =>
        prev.filter((sv) => !videos.some((v) => v.videoId === sv.videoId))
      );
    } else {
      // Select all from current search
      setSelectedVideos((prev) => {
        const newVideos = videos.filter(
          (v) => !prev.some((pv) => pv.videoId === v.videoId)
        );
        return [...prev, ...newVideos];
      });
    }
  }, [videos, selectedVideos]);

  const handleClearSelection = useCallback(() => {
    setSelectedVideos([]);
  }, []);

  const handleProcessSelected = useCallback(() => {
    if (selectedVideos.length > 0) {
      onProcessVideos(selectedVideos);
    }
  }, [selectedVideos, onProcessVideos]);

  const isVideoSelected = (videoId: string) =>
    selectedVideos.some((v) => v.videoId === videoId);

  const allCurrentSelected =
    videos.length > 0 &&
    videos.every((v) => selectedVideos.some((sv) => sv.videoId === v.videoId));

  const getPlaceholder = () => {
    switch (searchType) {
      case "user":
        return "Enter TikTok username (e.g., @chefgordon)";
      case "hashtag":
        return "Enter hashtag (e.g., #pastarecipe)";
      default:
        return "Search for recipe videos (e.g., pasta, chicken curry, dessert)";
    }
  };

  return (
    <div className={styles.container}>
      {/* Search Type Selector */}
      <div className={styles.searchTypeSelector}>
        <button
          type="button"
          className={`${styles.searchTypeBtn} ${searchType === "keywords" ? styles.active : ""}`}
          onClick={() => setSearchType("keywords")}
          disabled={disabled || isLoading}
        >
          Keywords
        </button>
        <button
          type="button"
          className={`${styles.searchTypeBtn} ${searchType === "user" ? styles.active : ""}`}
          onClick={() => setSearchType("user")}
          disabled={disabled || isLoading}
        >
          User
        </button>
        <button
          type="button"
          className={`${styles.searchTypeBtn} ${searchType === "hashtag" ? styles.active : ""}`}
          onClick={() => setSearchType("hashtag")}
          disabled={disabled || isLoading}
        >
          Hashtag
        </button>
      </div>

      <form onSubmit={handleSearch} className={styles.searchForm}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={getPlaceholder()}
          className={styles.searchInput}
          disabled={disabled || isLoading}
        />
        <button
          type="submit"
          className={styles.searchButton}
          disabled={disabled || isLoading || !query.trim()}
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </form>

      {/* Recent Users - show when user search type is selected */}
      {searchType === "user" && recentUsers.length > 0 && (
        <div className={styles.recentItems}>
          <span className={styles.recentLabel}>Recent:</span>
          {recentUsers.map((user) => (
            <div key={user} className={styles.recentItem}>
              <button
                type="button"
                className={styles.recentItemBtn}
                onClick={() => handleRecentClick(user)}
                disabled={disabled || isLoading}
              >
                @{user}
              </button>
              <button
                type="button"
                className={styles.recentItemRemove}
                onClick={() => handleRemoveRecent(user, "user")}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Recent Hashtags - show when hashtag search type is selected */}
      {searchType === "hashtag" && recentHashtags.length > 0 && (
        <div className={styles.recentItems}>
          <span className={styles.recentLabel}>Recent:</span>
          {recentHashtags.map((tag) => (
            <div key={tag} className={styles.recentItem}>
              <button
                type="button"
                className={styles.recentItemBtn}
                onClick={() => handleRecentClick(tag)}
                disabled={disabled || isLoading}
              >
                #{tag}
              </button>
              <button
                type="button"
                className={styles.recentItemRemove}
                onClick={() => handleRemoveRecent(tag, "hashtag")}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Selection bar */}
      {selectedVideos.length > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {selectedVideos.length} video{selectedVideos.length !== 1 ? "s" : ""} selected
          </span>
          <div className={styles.selectionActions}>
            <button
              onClick={handleClearSelection}
              className={styles.clearButton}
              disabled={disabled}
            >
              Clear All
            </button>
            <button
              onClick={handleProcessSelected}
              className={styles.processButton}
              disabled={disabled}
            >
              Extract Dishes ({selectedVideos.length})
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Searching TikTok...</span>
        </div>
      )}

      {!isLoading && hasSearched && videos.length === 0 && !error && (
        <div className={styles.noResults}>
          No videos found. Try a different search term.
        </div>
      )}

      {!isLoading && videos.length > 0 && (
        <div className={styles.results}>
          <div className={styles.resultsHeader}>
            <p className={styles.resultsCount}>
              Found {videos.length} videos - select videos to extract dish images
            </p>
            <button
              onClick={handleSelectAll}
              className={styles.selectAllButton}
              disabled={disabled}
            >
              {allCurrentSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div className={styles.grid}>
            {videos.map((video) => (
              <VideoCard
                key={video.videoId}
                video={video}
                isSelected={isVideoSelected(video.videoId)}
                onToggleSelect={handleToggleSelect}
                disabled={disabled}
                isUsed={usedVideoIds.has(video.videoId)}
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className={styles.loadMoreContainer}>
              <button
                onClick={handleLoadMore}
                className={styles.loadMoreButton}
                disabled={disabled || isLoadingMore}
              >
                {isLoadingMore ? "Loading..." : "Load More Videos"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Show saved selections when no search results */}
      {!isLoading && !hasSearched && selectedVideos.length > 0 && (
        <div className={styles.savedSelection}>
          <h3 className={styles.savedTitle}>Your Selected Videos</h3>
          <p className={styles.savedSubtitle}>
            These videos are saved and ready to process
          </p>
          <div className={styles.grid}>
            {selectedVideos.map((video) => (
              <VideoCard
                key={video.videoId}
                video={video}
                isSelected={true}
                onToggleSelect={handleToggleSelect}
                disabled={disabled}
                isUsed={usedVideoIds.has(video.videoId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
